import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import { DsToastService } from '@bmw-ds/components';
import { NGXLogger } from 'ngx-logger';

import { DealerProvider } from '../../../../auth/dealer/dealer.provider';
import { RetailCrmService } from '../../../customer/clients/retail-crm/retail-crm.service';
import {
  C2vOperation,
  CustomerVehicle,
  CustomerVehicleRelationType,
  EditVehicleRequest,
} from '../../../customer/model/customer-vehicle.model';
import { Customer } from '../../../customer/model/customer.model';
import { FeatureVisibilityService } from '../../../customer/services/feature-visibility.service';
import { useI18n } from '../../../i18n/services/i18n.service';
import { AddRelationModalWrapper } from './add-relation-modal.wrapper';
import { AddVehicleModalWrapper } from './add-vehicle-modal.wrapper';
import { EditLicensePlateModalWrapper } from './edit-license-plate-modal.wrapper';
import { RemoveRelationModalWrapper } from './remove-relation-modal.wrapper';
import { RemoveVehicleModalWrapper } from './remove-vehicle-modal.wrapper';
import { VehiclesComponent } from './vehicles.component';
import { RemoveRelation, VehicleFeatures } from './vehicles.view-model';

@Component({
  selector: 'cdf-vehicles-container',
  imports: [
    RemoveRelationModalWrapper,
    RemoveVehicleModalWrapper,
    VehiclesComponent,
    AddRelationModalWrapper,
    AddVehicleModalWrapper,
    EditLicensePlateModalWrapper,
  ],
  template: `
    <cdf-vehicles
      [enableVehicleSelection]="enableVehicleSelection()"
      [readonly]="readonly()"
      [customer]="customer()"
      [activeCountry]="dealerProvider.activeCountry()"
      [features]="features()"
      (selectVehicle)="selectVehicle.emit($event)"
      (showRemoveRelationModal)="showRemoveRelationModal($event.vehicle, $event.relation)"
      (showRemoveVehicleModal)="showRemoveVehicleModal($event)"
      (showAddRelationModal)="showAddRelationModal($event)"
      (showAddVehicleModal)="showAddVehicleModal()"
      (showEditLicensePlateModal)="showEditLicensePlateModal($event)"
    />

    @if (currentRemoveRelation(); as removeRelation) {
      <cdf-remove-relation-modal
        [vehicle]="removeRelation.vehicle"
        [relationToRemove]="removeRelation.relation"
        [loading]="requestInFlight()"
        (confirmRemove)="removeVehicleRelation(removeRelation.vehicle, removeRelation.relation)"
        (hideModal)="hideRemoveRelationModal()"
        (switchToRemoveVehicleModal)="onSwitchToRemoveVehicleModal(removeRelation.vehicle)"
      />
    }

    @if (currentRemoveVehicle(); as vehicle) {
      <cdf-remove-vehicle-modal
        [vehicle]="vehicle"
        [loading]="requestInFlight()"
        (confirmRemove)="removeVehicle(vehicle)"
        (hideModal)="hideRemoveVehicleModal()"
      />
    }

    @if (currentAddRelation(); as vehicle) {
      <cdf-add-relation-modal
        [vehicle]="vehicle"
        [loading]="requestInFlight()"
        (hideModal)="hideAddRelationModal()"
        (confirmAdd)="addRelation(vehicle, $event)"
      />
    }

    @if (currentAddVehicle()) {
      <cdf-add-vehicle-modal-wrapper
        [loading]="requestInFlight()"
        [accountNumber]="customer().accountNumber"
        (openChange)="$event === false && hideAddVehicleModal()"
        (confirmAdd)="addVehicle($event.vehicle, $event.relations)"
      />
    }

    @if (currentEditLicensePlateVehicle(); as vehicle) {
      <cdf-edit-license-plate-modal
        [vehicle]="vehicle"
        [loading]="requestInFlight()"
        (confirmEdit)="editLicensePlate(vehicle, $event)"
        (hideModal)="hideEditLicensePlateModal()"
      />
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VehiclesContainer {
  private readonly i18n = useI18n();
  private readonly logger = inject(NGXLogger);
  private readonly retailCrmService = inject(RetailCrmService);
  private readonly dsToastService = inject(DsToastService);
  protected readonly featureVisibilityService = inject(FeatureVisibilityService);
  protected readonly dealerProvider = inject(DealerProvider);

  readonly customer = model.required<Customer>();
  readonly readonly = input(false, { transform: booleanAttribute });
  readonly enableVehicleSelection = input(false);

  readonly requestInFlight = signal(false);

  readonly selectVehicle = output<string>();

  protected readonly features = computed(
    (): VehicleFeatures => ({
      removeVehicles: !this.featureVisibilityService.isHidden(C2vOperation.REMOVE_VEHICLES),
      removeRelations: !this.featureVisibilityService.isHidden(C2vOperation.REMOVE_RELATIONS),
      addRelations: !this.featureVisibilityService.isHidden(C2vOperation.ADD_RELATIONS),
      addVehicles: !this.featureVisibilityService.isHidden(C2vOperation.ADD_VEHICLES),
      editLicensePlate: !this.featureVisibilityService.isHidden(C2vOperation.EDIT_LICENSE_PLATE),
    }),
  );

  //region Remove relation
  /** When set, the modal for removing the relation is shown */
  readonly currentRemoveRelation = signal<RemoveRelation | undefined>(undefined);

  protected showRemoveRelationModal(vehicle: CustomerVehicle, relation: CustomerVehicleRelationType) {
    this.currentRemoveRelation.set({ vehicle, relation: relation });
  }

  protected hideRemoveRelationModal() {
    this.currentRemoveRelation.set(undefined);
  }

  protected async removeVehicleRelation(vehicle: CustomerVehicle, relation: CustomerVehicleRelationType) {
    const toastId = 'cdf-vehicles-container-remove-vehicle-relation-toast';

    const customer = this.customer();

    const changeRequest: EditVehicleRequest = {
      vin: vehicle.vin,
      accountNumber: customer.accountNumber,
      addRelationTypes: new Set(),
      removeRelationTypes: new Set([relation]),
    };

    try {
      this.dsToastService.popToast(toastId);
      this.requestInFlight.set(true);
      await this.retailCrmService.updateVehicleC2v(changeRequest);

      // Update customer with adapted vehicle relations.
      this.customer.set(this.applyUpdate(customer, changeRequest));
    } catch (e) {
      this.logger.error('#CDFTF0231 Error while removing vehicle relation', e);
      this.dsToastService.pushToast({
        id: toastId,
        toastTitle: this.i18n('vehicles.removeRelationVerifyModal.toastFailedTitle'),
        toastText: this.i18n('vehicles.removeRelationVerifyModal.toastFailedText'),
        tone: 'critical',
      });
    } finally {
      this.requestInFlight.set(false);
      this.hideRemoveRelationModal();
    }
  }

  onSwitchToRemoveVehicleModal(vehicle: CustomerVehicle) {
    this.hideRemoveRelationModal();
    this.showRemoveVehicleModal(vehicle);
  }
  //endregion

  //region Remove vehicle
  /** When set, the modal for removing the vehicle is shown */
  readonly currentRemoveVehicle = signal<CustomerVehicle | undefined>(undefined);

  protected showRemoveVehicleModal(vehicle: CustomerVehicle) {
    this.currentRemoveVehicle.set(vehicle);
  }

  protected hideRemoveVehicleModal() {
    this.currentRemoveVehicle.set(undefined);
  }

  /**
   * Removes the passed vehicle from the customer.
   */
  protected async removeVehicle(vehicle: CustomerVehicle) {
    const toastId = 'cdf-vehicles-container-remove-vehicle-toast';

    const customer = this.customer();

    const changeRequest: EditVehicleRequest = {
      vin: vehicle.vin,
      accountNumber: customer.accountNumber,
      addRelationTypes: new Set(),
      removeRelationTypes: new Set(vehicle.relationTypes),
    };

    try {
      this.dsToastService.popToast(toastId);
      this.requestInFlight.set(true);
      await this.retailCrmService.updateVehicleC2v(changeRequest);

      // Update customer by removing the vehicle from the list
      this.customer.set(this.applyUpdate(customer, changeRequest));
    } catch (e) {
      this.logger.error('#CDFTF0232 Error while removing vehicle from customer', e);
      this.dsToastService.pushToast({
        id: toastId,
        toastTitle: this.i18n('vehicles.removeVehicleVerifyModal.toastFailedTitle'),
        toastText: this.i18n('vehicles.removeVehicleVerifyModal.toastFailedText'),
        tone: 'critical',
      });
    } finally {
      this.requestInFlight.set(false);
      this.hideRemoveVehicleModal();
    }
  }
  //endregion

  //region Add relation
  /** When set, the modal for adding a relation is shown */
  readonly currentAddRelation = signal<CustomerVehicle | undefined>(undefined);

  protected showAddRelationModal(vehicle: CustomerVehicle) {
    this.currentAddRelation.set(vehicle);
  }

  protected hideAddRelationModal() {
    this.currentAddRelation.set(undefined);
  }

  /**
   * Removes the passed vehicle from the customer.
   */
  protected async addRelation(vehicle: CustomerVehicle, relations: ReadonlySet<CustomerVehicleRelationType>) {
    const toastId = 'cdf-vehicles-container-add-relation-toast';

    const customer = this.customer();

    const changeRequest: EditVehicleRequest = {
      vin: vehicle.vin,
      accountNumber: customer.accountNumber,
      addRelationTypes: relations,
      removeRelationTypes: new Set(),
    };

    try {
      this.dsToastService.popToast(toastId);
      this.requestInFlight.set(true);
      await this.retailCrmService.updateVehicleC2v(changeRequest);

      this.customer.set(this.applyUpdate(customer, changeRequest));
    } catch (e) {
      this.logger.error('#CDFTF0248 Error while adding relation to customer', relations, e);
      this.dsToastService.pushToast({
        id: toastId,
        toastTitle: this.i18n('vehicles.addRelationVerifyModal.toastFailedTitle', { count: relations.size }),
        toastText: this.i18n('vehicles.addRelationVerifyModal.toastFailedText', { count: relations.size }),
        tone: 'critical',
      });
    } finally {
      this.requestInFlight.set(false);
      this.hideAddRelationModal();
    }
  }
  //endregion

  //region Add vehicle
  /** When set, the modal for adding a relation is shown */
  readonly currentAddVehicle = signal(false);

  protected showAddVehicleModal() {
    this.currentAddVehicle.set(true);
  }

  protected hideAddVehicleModal() {
    this.currentAddVehicle.set(false);
  }

  /**
   * Removes the passed vehicle from the customer.
   */
  protected async addVehicle(vehicle: CustomerVehicle, relations: ReadonlySet<CustomerVehicleRelationType>) {
    const toastId = 'cdf-vehicles-container-add-vehicle-toast';

    const customer = this.customer();

    const changeRequest: EditVehicleRequest = {
      vin: vehicle.vin,
      accountNumber: customer.accountNumber,
      addRelationTypes: relations,
      removeRelationTypes: new Set(),
    };

    try {
      this.dsToastService.popToast(toastId);
      this.requestInFlight.set(true);
      await this.retailCrmService.updateVehicleC2v(changeRequest);

      this.customer.set(this.applyUpdate(customer, changeRequest, vehicle));
    } catch (e) {
      this.logger.error('#CDFTF0249 Error while adding vehicle to customer', relations, e);
      this.dsToastService.pushToast({
        id: toastId,
        toastTitle: this.i18n('vehicles.addVehicleModal.toastFailedTitle'),
        toastText: this.i18n('vehicles.addVehicleModal.toastFailedText'),
        tone: 'critical',
      });
    } finally {
      this.requestInFlight.set(false);
      this.hideAddVehicleModal();
    }
  }
  //endregion

  //region Edit license plate
  /** When set, the modal for editing the license plate is shown */
  readonly currentEditLicensePlateVehicle = signal<CustomerVehicle | undefined>(undefined);

  protected showEditLicensePlateModal(vehicle: CustomerVehicle) {
    this.currentEditLicensePlateVehicle.set(vehicle);
  }

  protected hideEditLicensePlateModal() {
    this.currentEditLicensePlateVehicle.set(undefined);
  }

  protected async editLicensePlate(vehicle: CustomerVehicle, registrationNumber: string) {
    const toastId = 'cdf-vehicles-container-edit-license-plate-toast';

    const customer = this.customer();

    const changeRequest: EditVehicleRequest = {
      vin: vehicle.vin,
      accountNumber: customer.accountNumber,
      addRelationTypes: new Set(),
      removeRelationTypes: new Set(),
      registrationNumber,
    };

    try {
      this.dsToastService.popToast(toastId);
      this.requestInFlight.set(true);
      await this.retailCrmService.updateVehicleC2v(changeRequest);

      this.customer.set(this.applyUpdate(customer, changeRequest));
    } catch (e) {
      this.logger.error('#CDFTF0256 Error while updating licence plate', vehicle.vin, e);
      this.dsToastService.pushToast({
        id: toastId,
        toastTitle: this.i18n('vehicles.licensePlate.editModal.toastFailedTitle'),
        toastText: this.i18n('vehicles.licensePlate.editModal.toastFailedText'),
        tone: 'critical',
      });
    } finally {
      this.requestInFlight.set(false);
      this.hideEditLicensePlateModal();
    }
  }
  //endregion

  private applyUpdate(customer: Customer, changeRequest: EditVehicleRequest, newVehicle?: CustomerVehicle): Customer {
    if (!customer.vehicles && !newVehicle) return customer;

    const vehicles = [...(customer.vehicles ?? []), ...(newVehicle ? [newVehicle] : [])]
      .map((vehicle) => {
        if (changeRequest.vin !== vehicle.vin) return vehicle;

        const newRelationTypes = vehicle.relationTypes
          .filter((relation) => !changeRequest.removeRelationTypes.has(relation))
          .concat(...changeRequest.addRelationTypes);

        if (newRelationTypes.length === 0) return undefined;

        return {
          ...vehicle,
          relationTypes: newRelationTypes,
          ...(changeRequest.registrationNumber !== undefined && { licensePlate: changeRequest.registrationNumber }),
        };
      })
      .filter((v): v is CustomerVehicle => v !== undefined);

    return { ...customer, vehicles };
  }
}
